import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  IconButton,
  SimpleGrid,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
} from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { FaCheckCircle, FaExclamationCircle, FaTrash } from 'react-icons/fa';
import { MdCloudUpload, MdDownload, MdInsertDriveFile, MdSwapHoriz } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import Card from 'components/card/Card';
import { createWithdrawalBatch } from 'services/api.service';
import { BatchItemDraft, downloadTemplate, parseFile } from './parse';

const brl = (v: number) => (v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const dash = <Text as="span" color="gray.300">—</Text>;

export default function WithdrawalsBatchUpload() {
  const [rows, setRows] = useState<BatchItemDraft[]>([]);
  const [fileName, setFileName] = useState<string | undefined>();
  const toast = useToast();
  const navigate = useNavigate();

  const onDrop = async (files: File[]) => {
    const file = files[0];
    if (!file) return;
    try {
      setRows(await parseFile(file));
      setFileName(file.name);
    } catch {
      toast({
        title: 'Erro ao ler o arquivo',
        description: 'Confira o formato e as colunas do modelo.',
        status: 'error',
        position: 'top',
      });
    }
  };

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    multiple: false,
    accept: '.csv,.xls,.xlsx',
  });

  const errorCount = useMemo(() => rows.filter((r) => r.errors.length > 0).length, [rows]);
  const hasErrors = errorCount > 0;
  const total = useMemo(() => rows.reduce((s, r) => s + (r.amount || 0), 0), [rows]);
  const removeRow = (idx: number) => setRows((rs) => rs.filter((_, i) => i !== idx));

  const { mutate: create, isLoading } = useMutation(
    () =>
      createWithdrawalBatch({
        fileName,
        items: rows.map((r) => ({
          externalId: r.externalId,
          pixKey: r.pixKey,
          amount: r.amount,
          name: r.name,
          document: r.document,
          pixKeyType: r.pixKeyType,
          description: r.description,
        })),
      }),
    {
      onSuccess: (res) => {
        toast({ title: 'Lote criado', status: 'success', position: 'top' });
        navigate(`/access/withdrawals-batch/${res.batchId}`);
      },
      onError: (e: any) => {
        toast({
          title: 'Erro ao criar lote',
          description: e?.response?.data?.message,
          status: 'error',
          position: 'top',
        });
      },
    },
  );

  const loaded = rows.length > 0;

  return (
    <Box>
      {/* Cabeçalho */}
      <Flex
        justify="space-between"
        align={{ base: 'flex-start', md: 'center' }}
        direction={{ base: 'column', md: 'row' }}
        gap={3}
        mb={5}
      >
        <Box>
          <Text fontSize="2xl" fontWeight="bold" color="navy.700">
            Novo lote de saques
          </Text>
          <Text color="gray.500" fontSize="sm">
            Importe um CSV ou Excel com os destinatários, revise os itens e crie o lote.
          </Text>
        </Box>
        <Button
          leftIcon={<Icon as={MdDownload} boxSize={5} />}
          variant="outline"
          colorScheme="brand"
          onClick={downloadTemplate}
        >
          Baixar modelo
        </Button>
      </Flex>

      {/* Upload */}
      <Card p="10px" mb={5}>
        {!loaded ? (
          <Flex
            {...getRootProps()}
            direction="column"
            align="center"
            justify="center"
            textAlign="center"
            px={6}
            py={12}
            cursor="pointer"
            bg={isDragActive ? 'brand.100' : 'gray.50'}
            border="2px dashed"
            borderColor={isDragActive ? 'brand.500' : 'secondaryGray.100'}
            borderRadius="16px"
            transition="all .15s ease"
            _hover={{ borderColor: 'brand.500', bg: 'brand.100' }}
          >
            <input {...getInputProps()} />
            <Flex
              align="center"
              justify="center"
              boxSize="56px"
              borderRadius="full"
              bg="brand.500"
              color="white"
              mb={4}
              boxShadow="0 8px 20px rgba(0,0,0,0.12)"
            >
              <Icon as={MdCloudUpload} boxSize={7} />
            </Flex>
            <Text fontWeight="bold" color="navy.700">
              {isDragActive ? 'Solte o arquivo aqui' : 'Arraste o arquivo ou clique para selecionar'}
            </Text>
            <Text color="gray.500" fontSize="sm" mt={1} maxW="lg">
              CSV (separado por <b>;</b>) ou Excel. Colunas: <b>nome; documento; chave_pix; valor; identificador; tipo_chave; descricao</b>.
            </Text>
            <HStack mt={4} spacing={2}>
              {['.csv', '.xlsx', '.xls'].map((ext) => (
                <Badge key={ext} colorScheme="gray" variant="subtle" px={2} py={1} borderRadius="md" textTransform="lowercase">
                  {ext}
                </Badge>
              ))}
            </HStack>
          </Flex>
        ) : (
          <Flex align="center" justify="space-between" px={3} py={2} gap={3} flexWrap="wrap">
            <input {...getInputProps()} />
            <HStack spacing={3} minW={0}>
              <Flex align="center" justify="center" boxSize="42px" borderRadius="12px" bg="brand.100" color="brand.600" flexShrink={0}>
                <Icon as={MdInsertDriveFile} boxSize={5} />
              </Flex>
              <Box minW={0}>
                <Text fontWeight="600" color="navy.700" noOfLines={1}>
                  {fileName ?? 'arquivo'}
                </Text>
                <Text fontSize="xs" color="gray.500">
                  {rows.length} linha(s) lida(s)
                </Text>
              </Box>
            </HStack>
            <Button leftIcon={<Icon as={MdSwapHoriz} boxSize={5} />} size="sm" variant="ghost" colorScheme="brand" onClick={open}>
              Trocar arquivo
            </Button>
          </Flex>
        )}
      </Card>

      <Card p="16px" mb={5}>
        <Text fontWeight="bold" color="navy.700" mb={2}>Formato da chave Pix por tipo</Text>
        <Text color="gray.500" fontSize="sm" mb={3}>
          A coluna <b>tipo_chave</b> é opcional (valores: <b>celular, evp, cpf, cnpj, email</b>). Se ficar vazia, o tipo é
          detectado automaticamente pela chave. Se preenchida, validamos que a chave corresponde ao tipo.
        </Text>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
          {[
            ['celular', 'DDD + número, ex. (11) 99999-9999 (salvo como +5511999999999)'],
            ['evp', 'Chave aleatória (UUID), ex. 123e4567-e89b-12d3-a456-426614174000'],
            ['cpf', '11 dígitos, ex. 123.456.789-00'],
            ['cnpj', '14 dígitos, ex. 12.345.678/0001-90'],
            ['email', 'ex. fulano@email.com'],
          ].map(([t, desc]) => (
            <HStack key={t} align="start" spacing={2}>
              <Badge colorScheme="brand" variant="subtle" textTransform="lowercase">{t}</Badge>
              <Text fontSize="sm" color="gray.600">{desc}</Text>
            </HStack>
          ))}
        </SimpleGrid>
      </Card>

      {loaded && (
        <>
          {/* Resumo + ação */}
          <Flex
            justify="space-between"
            align={{ base: 'stretch', lg: 'center' }}
            direction={{ base: 'column', lg: 'row' }}
            gap={4}
            mb={4}
          >
            <SimpleGrid columns={3} spacing={3} flex="1" maxW={{ lg: '560px' }}>
              <Card py={3} px={4}>
                <Text fontSize="xs" color="gray.500">Itens</Text>
                <Text fontSize="xl" fontWeight="bold" color="navy.700">{rows.length}</Text>
              </Card>
              <Card py={3} px={4}>
                <Text fontSize="xs" color="gray.500">Total</Text>
                <Text fontSize="xl" fontWeight="bold" color="navy.700">{brl(total)}</Text>
              </Card>
              <Card py={3} px={4}>
                <Text fontSize="xs" color="gray.500">Com erro</Text>
                <Text fontSize="xl" fontWeight="bold" color={hasErrors ? 'red.500' : 'green.500'}>{errorCount}</Text>
              </Card>
            </SimpleGrid>
            <Button
              colorScheme="brand"
              size="lg"
              px={10}
              alignSelf={{ base: 'stretch', lg: 'center' }}
              isDisabled={hasErrors || rows.length === 0}
              isLoading={isLoading}
              onClick={() => create()}
            >
              Criar lote
            </Button>
          </Flex>

          {hasErrors && (
            <Alert status="warning" borderRadius="12px" mb={4} variant="left-accent">
              <AlertIcon />
              <Text fontSize="sm">
                {errorCount} linha(s) com erro. Corrija no arquivo e reenvie, ou remova as linhas destacadas antes de criar o lote.
              </Text>
            </Alert>
          )}

          {/* Pré-visualização */}
          <Card p="0px" overflow="hidden">
            <Box overflowX="auto">
              <Table size="sm" variant="simple">
                <Thead bg="gray.50">
                  <Tr>
                    <Th w="44px" />
                    <Th>Identificador</Th>
                    <Th>Nome</Th>
                    <Th>Documento</Th>
                    <Th>Chave Pix</Th>
                    <Th>Tipo</Th>
                    <Th>Descrição</Th>
                    <Th isNumeric>Valor</Th>
                    <Th>Erros</Th>
                    <Th w="64px" textAlign="right">Ações</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {rows.map((r, i) => {
                    const bad = r.errors.length > 0;
                    return (
                      <Tr
                        key={i}
                        bg={bad ? 'red.50' : undefined}
                        _hover={{ bg: bad ? 'red.100' : 'gray.50' }}
                        transition="background .1s ease"
                      >
                        <Td>
                          <Icon as={bad ? FaExclamationCircle : FaCheckCircle} color={bad ? 'red.500' : 'green.500'} boxSize={4} />
                        </Td>
                        <Td fontWeight="500" color="navy.700">{r.externalId || dash}</Td>
                        <Td>{r.name || dash}</Td>
                        <Td>{r.document || dash}</Td>
                        <Td>{r.pixKey || dash}</Td>
                        <Td>{r.pixKeyType || dash}</Td>
                        <Td>{r.description || dash}</Td>
                        <Td isNumeric fontWeight="600" color="navy.700">{brl(r.amount)}</Td>
                        <Td>
                          {bad ? (
                            <HStack spacing={1} flexWrap="wrap">
                              {r.errors.map((e) => (
                                <Badge key={e} colorScheme="red" variant="subtle">{e}</Badge>
                              ))}
                            </HStack>
                          ) : (
                            dash
                          )}
                        </Td>
                        <Td textAlign="right">
                          <Tooltip label="Remover linha" hasArrow>
                            <IconButton
                              aria-label="Remover linha"
                              icon={<Icon as={FaTrash} />}
                              size="sm"
                              variant="ghost"
                              colorScheme="red"
                              onClick={() => removeRow(i)}
                            />
                          </Tooltip>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
          </Card>
        </>
      )}
    </Box>
  );
}
